import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { PlatformUser } from "@oms/types";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@oms/ui";
import { PageShell } from "@/components/pages/page-shell";
import { SearchField } from "@/components/ui/search-field";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/stores/auth-store";

export const Route = createFileRoute("/app/admin")({
  beforeLoad: () => {
    if (!useAuthStore.getState().isSuperAdmin) {
      console.log(useAuthStore.getState())
      throw redirect({ to: "/app" });
    }
  },
  component: AdminPage,
});

function AdminPage() {
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const currentUserId = useAuthStore((s) => s.user?.id);

  const usersQuery = useQuery({
    queryKey: ["admin", "users", search],
    queryFn: async () => {
      const response = await adminApi.listUsers({ search, pageSize: 50 });
      return response.data ?? [];
    },
  });

  const toggleSuperAdmin = useMutation({
    mutationFn: async (user: PlatformUser) =>
      adminApi.updateUser(user.id, { isSuperAdmin: !user.isSuperAdmin }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });

  const impersonate = useMutation({
    mutationFn: async (user: PlatformUser) =>
      adminApi.impersonate({
        userId: user.id,
        reason: "Support: resolving a stuck user issue",
      }),
    onSuccess: ({ data }) => {
      if (!data) return;
      setAuth(data.tokens, data.session);
      void queryClient.invalidateQueries();
      void navigate({ to: "/app" });
    },
  });

  return (
    <PageShell>
      <PageHeader
        title="Platform Admin"
        description="Superadmin-only: view and manage every user and organization across the platform."
      />

      <Card>
        <CardHeader
          title="Users"
          description="Search across all organizations. Impersonation issues an audit-logged, org-scoped session so you can reproduce a stuck user's issue."
        />
        <CardBody className="space-y-4">
          <SearchField
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {usersQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : usersQuery.isError ? (
            <ErrorState
              title="Failed to load users"
              description="Something went wrong fetching the platform user list."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Organizations</th>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(usersQuery.data ?? []).map((user) => (
                    <tr key={user.id} className="border-b border-border-muted">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-text">
                          {user.name}
                        </div>
                        <div className="text-xs text-text-muted">
                          {user.email}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {user.organizations.length === 0 ? (
                            <span className="text-xs text-text-subtle">
                              No organizations
                            </span>
                          ) : (
                            user.organizations.map((m) => (
                              <Badge key={m.organizationId} variant="neutral">
                                {m.organizationName} · {m.role}
                              </Badge>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        {user.isSuperAdmin ? (
                          <Badge variant="confirmed">Superadmin</Badge>
                        ) : (
                          <span className="text-xs text-text-subtle">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={
                              user.organizations.length === 0 ||
                              impersonate.isPending
                            }
                            onClick={() => impersonate.mutate(user)}
                          >
                            Impersonate
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={
                              user.id === currentUserId ||
                              toggleSuperAdmin.isPending
                            }
                            onClick={() => toggleSuperAdmin.mutate(user)}
                          >
                            {user.isSuperAdmin
                              ? "Revoke superadmin"
                              : "Grant superadmin"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </PageShell>
  );
}
